import { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { MOVED, movedTarget } from "./nav";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Toaster } from "sonner";
import { AuthProvider, RequireAuth, useAuth } from "./auth";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import MyDay from "./pages/MyDay";
import Website from "./pages/site/Website";
import HomePage from "./pages/home/HomePage";

const NotFound = lazy(() => import("./pages/NotFound"));
const Help = lazy(() => import("./pages/Help"));
const Training = lazy(() => import("./pages/Training"));
const Crm = lazy(() => import("./pages/Crm"));
const Bookings = lazy(() => import("./pages/Bookings"));
const PublicBooking = lazy(() => import("./pages/PublicBooking"));
const Quotes = lazy(() => import("./pages/Quotes"));
const QuoteDetail = lazy(() => import("./pages/QuoteDetail"));
const Jobs = lazy(() => import("./pages/Jobs"));
const JobRoom = lazy(() => import("./pages/JobRoom"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectRoom = lazy(() => import("./pages/ProjectRoom"));
const Calendar = lazy(() => import("./pages/Calendar"));
const Customers = lazy(() => import("./pages/Customers"));
const CustomerDetail = lazy(() => import("./pages/CustomerDetail"));
const Team = lazy(() => import("./pages/Team"));
const TeamDetail = lazy(() => import("./pages/TeamDetail"));
const Subcontractors = lazy(() => import("./pages/Subcontractors"));
const SubcontractorDetail = lazy(() => import("./pages/SubcontractorDetail"));
const Invoices = lazy(() => import("./pages/Invoices"));
const InvoiceDetail = lazy(() => import("./pages/InvoiceDetail"));
const Reports = lazy(() => import("./pages/Reports"));
const Intelligence = lazy(() => import("./pages/Intelligence"));
const Integrations = lazy(() => import("./pages/Integrations"));
const Settings = lazy(() => import("./pages/Settings"));
const Master = lazy(() => import("./pages/Master"));
const Messages = lazy(() => import("./pages/Messages"));
const Notifications = lazy(() => import("./pages/Notifications"));
const CustomerPortal = lazy(() => import("./pages/CustomerPortal"));
const SubPortal = lazy(() => import("./pages/SubPortal"));
const DemoSelector = lazy(() => import("./pages/DemoSelector"));
const DemoManager = lazy(() => import("./pages/DemoManager"));
const DemoHome = lazy(() => import("./pages/DemoHome"));
const ClockIn = lazy(() => import("./pages/ClockIn"));
const Supplies = lazy(() => import("./pages/Supplies"));
const Supply = lazy(() => import("./pages/Supply"));
const StaffDocuments = lazy(() => import("./pages/staff/StaffDocuments"));
const Social = lazy(() => import("./pages/Social"));
const Leave = lazy(() => import("./pages/Leave"));
const SupplierConfirm = lazy(() => import("./pages/SupplierConfirm"));
const Enquiry = lazy(() => import("./pages/Enquiry"));
const Product = lazy(() => import("./pages/home/pages/ProductPage"));
const Solutions = lazy(() => import("./pages/home/pages/SolutionsPage"));
const Trades = lazy(() => import("./pages/home/pages/TradesPage"));
const Pricing = lazy(() => import("./pages/home/pages/PricingPage"));
const About = lazy(() => import("./pages/home/pages/AboutPage"));
const BookDemo = lazy(() => import("./pages/home/pages/BookDemoPage"));
const Resources = lazy(() => import("./pages/home/pages/ResourcesPage"));
const Legal = lazy(() => import("./pages/home/pages/LegalPage"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const MarketplaceBusinessPage = lazy(() => import("./pages/Marketplace").then((m) => ({ default: m.MarketplaceBusinessPage })));
const MarketplaceOpportunityPage = lazy(() => import("./pages/Marketplace").then((m) => ({ default: m.MarketplaceOpportunityPage })));
const MarketplaceRequestPage = lazy(() => import("./pages/Marketplace").then((m) => ({ default: m.MarketplaceRequestPage })));
const MarketplaceWorkPage = lazy(() => import("./pages/Marketplace").then((m) => ({ default: m.MarketplaceWorkPage })));
const Present = lazy(() => import("./pages/Present"));
const Deals = lazy(() => import("./pages/Deals"));
const DealRoom = lazy(() => import("./pages/DealRoom"));
const Money = lazy(() => import("./pages/Money"));
const Payments = lazy(() => import("./pages/Payments"));
const Profitability = lazy(() => import("./pages/Profitability"));
const Variations = lazy(() => import("./pages/Variations"));
const Reviews = lazy(() => import("./pages/Reviews"));
const Join = lazy(() => import("./pages/Join"));

function Home() {
  const { user } = useAuth();
  if (user?.in_demo && !user.demo_persona) return <Navigate to="/demo/home" replace />;
  if (user?.in_demo && user.demo_persona?.kind === "client" && user.demo_persona.portal_path) return <Navigate to={user.demo_persona.portal_path} replace />;
  return user?.role === "team_member" ? <MyDay /> : <Dashboard />;
}

function RootSwitch() {
  const { user } = useAuth();
  if (user === null) return <div className="flex min-h-screen items-center justify-center" data-testid="auth-loading"><div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-300 border-t-zinc-900" /></div>;
  if (!user) return <HomePage />;
  return <Layout><Home /></Layout>;
}

function Missing() {
  const { user } = useAuth();
  if (user === null) return null;
  return user ? <Layout><NotFound /></Layout> : <Navigate to="/" replace />;
}

// Old URL → new home in the information architecture (query string preserved).
function Moved() {
  const loc = useLocation();
  return <Navigate to={movedTarget(loc.pathname, loc.search) || "/"} replace />;
}

function IndustriesMoved() {
  const loc = useLocation();
  return <Navigate to={`/trades${loc.search}${loc.hash}`} replace />;
}

function Boundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary location={location.pathname}>{children}</ErrorBoundary>;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  return user?.role === "admin" ? children : <Navigate to="/" replace />;
}

function MasterOnly({ children }) {
  const { user } = useAuth();
  return user?.is_master ? children : <Navigate to="/" replace />;
}

function StaffOnly({ children }) {
  const { user } = useAuth();
  return user?.role === "admin" || user?.role === "manager" ? children : <Navigate to="/" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors closeButton toastOptions={{ className: "!rounded-md !border !shadow-lg !font-medium", style: { fontFamily: "var(--font-ui)" } }} />
        <Boundary>
        <Suspense fallback={<div className="flex min-h-screen items-center justify-center" data-testid="route-loading"><div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-300 border-t-zinc-900" /></div>}>
        <Routes>
          <Route path="/" element={<RootSwitch />} />
          <Route path="/preview/home" element={<HomePage />} />
          <Route path="/legacy-home" element={<Website />} />
          <Route path="/industries" element={<IndustriesMoved />} />
          <Route path="/product" element={<Product />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/trades" element={<Trades />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/privacy" element={<Legal doc="privacy" />} />
          <Route path="/terms" element={<Legal doc="terms" />} />
          <Route path="/security" element={<Legal doc="security" />} />
          <Route path="/book-demo" element={<BookDemo />} />
          <Route path="/contact" element={<Navigate to="/book-demo?intent=speak" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/join/:token" element={<Join />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/portal/:token" element={<CustomerPortal />} />
          <Route path="/p/preview/:kind/:id" element={<RequireAuth><Present /></RequireAuth>} />
          <Route path="/p/:token" element={<Present />} />
          <Route path="/sub/:token" element={<SubPortal />} />
          <Route path="/po/:token" element={<SupplierConfirm />} />
          <Route path="/enquiry/:token" element={<Enquiry />} />
          <Route path="/book/:slug" element={<PublicBooking />} />
          <Route path="/demo/home" element={<RequireAuth><DemoHome /></RequireAuth>} />
          <Route element={<RequireAuth><Layout /></RequireAuth>}>
            <Route path="/dashboard" element={<Home />} />
            <Route path="/my-day" element={<MyDay />} />
            <Route path="/mobile" element={<MyDay />} />
            <Route path="/deals" element={<StaffOnly><Deals /></StaffOnly>} />
            <Route path="/deals/new" element={<Navigate to="/deals?new=1" replace />} />
            <Route path="/deals/pipeline" element={<StaffOnly><Crm /></StaffOnly>} />
            <Route path="/deals/bookings" element={<StaffOnly><Bookings /></StaffOnly>} />
            <Route path="/deals/quotes" element={<StaffOnly><Quotes /></StaffOnly>} />
            <Route path="/deals/:id" element={<StaffOnly><DealRoom /></StaffOnly>} />
            <Route path="/quotes/:id" element={<StaffOnly><QuoteDetail /></StaffOnly>} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/jobs/projects" element={<Projects />} />
            <Route path="/jobs/variations" element={<StaffOnly><Variations /></StaffOnly>} />
            <Route path="/jobs/:id" element={<JobRoom />} />
            <Route path="/projects/:id" element={<ProjectRoom />} />
            <Route path="/projects/:id/timeline" element={<ProjectRoom forceTab="timeline" />} />
            <Route path="/dispatch/schedule" element={<Calendar />} />
            <Route path="/dispatch/live" element={<StaffOnly><ClockIn forceTab="map" /></StaffOnly>} />
            <Route path="/dispatch/clock-in" element={<ClockIn />} />
            <Route path="/dispatch/timesheets" element={<ClockIn forceTab="timesheets" />} />
            <Route path="/dispatch/leave" element={<Leave />} />
            <Route path="/supplies" element={<Supplies />} />
            <Route path="/supply" element={<StaffOnly><Supply view="overview" /></StaffOnly>} />
            <Route path="/supply/materials" element={<StaffOnly><Supply view="materials" /></StaffOnly>} />
            <Route path="/supply/purchase-orders" element={<StaffOnly><Supply view="orders" /></StaffOnly>} />
            <Route path="/supply/deliveries" element={<StaffOnly><Supply view="deliveries" /></StaffOnly>} />
            <Route path="/supply/suppliers" element={<StaffOnly><Supply view="suppliers" /></StaffOnly>} />
            <Route path="/supply/insights" element={<StaffOnly><Supply view="insights" /></StaffOnly>} />
            <Route path="/documents" element={<StaffDocuments />} />
            <Route path="/team/subcontractors" element={<StaffOnly><Subcontractors /></StaffOnly>} />
            <Route path="/team/availability" element={<StaffOnly><Leave forceTab="availability" /></StaffOnly>} />
            <Route path="/subcontractors/:id" element={<StaffOnly><SubcontractorDetail /></StaffOnly>} />
            <Route path="/money" element={<StaffOnly><Money /></StaffOnly>} />
            <Route path="/money/invoices" element={<StaffOnly><Invoices /></StaffOnly>} />
            <Route path="/money/payments" element={<StaffOnly><Payments /></StaffOnly>} />
            <Route path="/money/profitability" element={<StaffOnly><Profitability /></StaffOnly>} />
            <Route path="/invoices/:id" element={<StaffOnly><InvoiceDetail /></StaffOnly>} />
            <Route path="/marketing/social" element={<StaffOnly><Social /></StaffOnly>} />
            <Route path="/marketing/reviews" element={<StaffOnly><Reviews /></StaffOnly>} />
            {Object.keys(MOVED).map((p) => <Route key={p} path={p} element={<Moved />} />)}
            <Route path="/customers" element={<StaffOnly><Customers /></StaffOnly>} />
            <Route path="/customers/:id" element={<StaffOnly><CustomerDetail /></StaffOnly>} />
            <Route path="/team" element={<Team />} />
            <Route path="/team/:id" element={<TeamDetail />} />
            <Route path="/reports" element={<StaffOnly><Reports /></StaffOnly>} />
            <Route path="/intelligence" element={<StaffOnly><Intelligence /></StaffOnly>} />
            <Route path="/assistant" element={<Navigate to="/intelligence" replace />} />
            <Route path="/ai" element={<Navigate to="/intelligence" replace />} />
            <Route path="/integrations" element={<StaffOnly><Integrations /></StaffOnly>} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/help" element={<Help />} />
            <Route path="/training" element={<Training />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/marketplace" element={<StaffOnly><Marketplace /></StaffOnly>} />
            <Route path="/marketplace/post" element={<StaffOnly><Marketplace forceTab="post" /></StaffOnly>} />
            <Route path="/marketplace/find" element={<StaffOnly><Marketplace forceTab="find" /></StaffOnly>} />
            <Route path="/marketplace/refer" element={<StaffOnly><Marketplace forceTab="refer" /></StaffOnly>} />
            <Route path="/marketplace/requests/:id" element={<StaffOnly><MarketplaceRequestPage /></StaffOnly>} />
            <Route path="/marketplace/opportunities/:id" element={<StaffOnly><MarketplaceOpportunityPage /></StaffOnly>} />
            <Route path="/marketplace/work/:id" element={<StaffOnly><MarketplaceWorkPage /></StaffOnly>} />
            <Route path="/marketplace/business/:id" element={<StaffOnly><MarketplaceBusinessPage /></StaffOnly>} />
            <Route path="/network" element={<Navigate to="/marketplace?tab=network" replace />} />
            <Route path="/master" element={<MasterOnly><Master /></MasterOnly>} />
            <Route path="/demo" element={<AdminOnly><DemoSelector /></AdminOnly>} />
            <Route path="/demo-selector" element={<Navigate to="/demo" replace />} />
            <Route path="/demo/manage" element={<AdminOnly><DemoManager /></AdminOnly>} />
          </Route>
          <Route path="*" element={<Missing />} />
        </Routes>
        </Suspense>
        </Boundary>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
